import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, onSnapshot, where, orderBy, doc, updateDoc, writeBatch, increment, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';

const styles = {
    container: {
        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        padding: '20px'
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px',
        flexWrap: 'wrap',
        gap: '16px'
    },
    title: {
        fontSize: '28px',
        fontWeight: '700',
        color: 'var(--text-primary)',
        letterSpacing: '-0.5px'
    },
    filterRow: {
        display: 'flex',
        gap: '16px',
        marginBottom: '24px',
        flexWrap: 'wrap',
        alignItems: 'center'
    },
    dateInput: {
        padding: '10px 14px',
        border: '1px solid var(--border-color)',
        borderRadius: '10px',
        fontSize: '14px',
        background: 'var(--bg-secondary)',
        color: 'var(--text-primary)',
        outline: 'none'
    },
    box: {
        background: 'var(--bg-primary)',
        padding: '24px',
        borderRadius: '16px',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
        border: '1px solid var(--border-color)'
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse'
    },
    th: {
        background: 'var(--bg-secondary)',
        padding: '14px 16px',
        textAlign: 'left',
        borderBottom: '1px solid var(--border-color)',
        color: 'var(--text-muted)',
        fontSize: '12px',
        fontWeight: '600',
        textTransform: 'uppercase'
    },
    td: {
        padding: '16px',
        borderBottom: '1px solid var(--border-color)',
        color: 'var(--text-secondary)',
        fontSize: '14px'
    },
    badge: {
        padding: '4px 10px',
        borderRadius: '6px',
        fontSize: '12px',
        fontWeight: '600',
        display: 'inline-block'
    },
    actionBtn: {
        padding: '6px 14px',
        borderRadius: '8px',
        border: '1px solid var(--border-color)',
        background: 'var(--bg-primary)',
        cursor: 'pointer',
        fontSize: '13px',
        fontWeight: '500',
        color: 'var(--text-secondary)',
        transition: 'all 0.2s'
    },
    // Modal styles
    modalOverlay: {
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 100,
        backdropFilter: 'blur(4px)'
    },
    modalContent: {
        background: 'var(--bg-primary)',
        padding: '32px',
        borderRadius: '16px',
        width: '90%',
        maxWidth: '600px',
        maxHeight: '80vh',
        overflowY: 'auto',
        boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
        border: '1px solid var(--border-color)',
        color: 'var(--text-primary)'
    },
    summaryCard: {
        background: 'var(--bg-primary)',
        borderRadius: '12px',
        padding: '16px 20px',
        border: '1px solid var(--border-color)',
        display: 'flex',
        alignItems: 'center',
        gap: '16px'
    }
};

export default function StoreSales() {
    const { userData, currentUser } = useAuth();
    const [sales, setSales] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedSale, setSelectedSale] = useState(null);
    const [showCancelConfirm, setShowCancelConfirm] = useState(null);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [actionLoading, setActionLoading] = useState(false);

    useEffect(() => {
        if (!userData || userData.role !== 'store' || !userData.companyId) return;

        const companyId = userData.companyId;
        const storeId = currentUser.uid;

        const salesQuery = query(
            collection(db, 'companies', companyId, 'retailSales'),
            where('storeId', '==', storeId),
            orderBy('saleDate', 'desc')
        );

        const unsub = onSnapshot(salesQuery, (snap) => {
            setSales(snap.docs.map(d => ({ id: d.id, ...d.data() })));
            setLoading(false);
        });

        return () => unsub();
    }, [userData, currentUser]);

    const filteredSales = useMemo(() => {
        if (!startDate && !endDate) return sales;

        return sales.filter(sale => {
            if (!sale.saleDate) return true;
            const saleDate = sale.saleDate.toDate();
            const start = startDate ? new Date(startDate + 'T00:00:00') : null;
            const end = endDate ? new Date(endDate + 'T23:59:59') : null;

            if (start && saleDate < start) return false;
            if (end && saleDate > end) return false;
            return true;
        });
    }, [sales, startDate, endDate]);

    const periodStats = useMemo(() => {
        const activeSales = filteredSales.filter(s => s.status !== 'cancelled');
        const total = activeSales.reduce((s, sale) => s + (sale.total || 0), 0);
        const count = activeSales.length;
        const cancelledCount = filteredSales.filter(s => s.status === 'cancelled').length;
        return { total, count, cancelledCount };
    }, [filteredSales]);

    const handleCancelSale = async (sale) => {
        setActionLoading(true);
        try {
            const companyId = userData.companyId;
            const storeId = currentUser.uid;

            const batch = writeBatch(db);

            // Update sale status to cancelled
            const saleRef = doc(db, 'companies', companyId, 'retailSales', sale.id);
            batch.update(saleRef, {
                status: 'cancelled',
                cancelledAt: serverTimestamp(),
                cancelledBy: { uid: currentUser.uid, email: currentUser.email }
            });

            // Return stock to store inventory
            (sale.items || []).forEach(item => {
                const invRef = doc(db, 'companies', companyId, 'stores', storeId, 'inventory', item.productId);
                batch.update(invRef, {
                    quantity: increment(item.quantity)
                });
            });

            await batch.commit();
            setShowCancelConfirm(null);
            setSelectedSale(null);
            alert('Satış başarıyla iptal edildi. Stoklar geri yüklendi.');
        } catch (error) {
            console.error('İptal hatası:', error);
            alert('Satış iptal edilemedi: ' + error.message);
        } finally {
            setActionLoading(false);
        }
    };

    if (loading) return <div style={styles.container}><p style={{color: 'var(--text-secondary)'}}>Yükleniyor...</p></div>;

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <h1 style={styles.title}>Satış Geçmişi</h1>
            </div>

            {/* Summary Statistics */}
            <div style={{ display: 'flex', gap: '16px', marginBottom: '24px', flexWrap: 'wrap' }}>
                <div style={styles.summaryCard}>
                    <span style={{ fontSize: '28px' }}>💰</span>
                    <div>
                        <div style={{ fontSize: '12px', color: 'var(--text-muted)', fontWeight: '600', textTransform: 'uppercase' }}>Toplam Ciro</div>
                        <div style={{ fontSize: '20px', fontWeight: '700', color: 'var(--text-primary)' }}>₺{periodStats.total.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</div>
                    </div>
                </div>
                <div style={styles.summaryCard}>
                    <span style={{ fontSize: '28px' }}>🧾</span>
                    <div>
                        <div style={{ fontSize: '12px', color: 'var(--text-muted)', fontWeight: '600', textTransform: 'uppercase' }}>Satış Sayısı</div>
                        <div style={{ fontSize: '20px', fontWeight: '700', color: 'var(--text-primary)' }}>{periodStats.count}</div>
                    </div>
                </div>
                {periodStats.cancelledCount > 0 && (
                    <div style={{ ...styles.summaryCard, borderColor: '#fecaca' }}>
                        <span style={{ fontSize: '28px' }}>❌</span>
                        <div>
                            <div style={{ fontSize: '12px', color: '#991b1b', fontWeight: '600', textTransform: 'uppercase' }}>İptal Edilen</div>
                            <div style={{ fontSize: '20px', fontWeight: '700', color: '#991b1b' }}>{periodStats.cancelledCount}</div>
                        </div>
                    </div>
                )}
            </div>

            {/* Filters */}
            <div style={styles.filterRow}>
                <label style={{ color: 'var(--text-secondary)', fontSize: '14px', fontWeight: '500' }}>Başlangıç:</label>
                <input style={styles.dateInput} type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                <label style={{ color: 'var(--text-secondary)', fontSize: '14px', fontWeight: '500' }}>Bitiş:</label>
                <input style={styles.dateInput} type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                {(startDate || endDate) && (
                    <button onClick={() => { setStartDate(''); setEndDate(''); }} style={{ ...styles.actionBtn, color: '#dc2626' }}>
                        ✕ Temizle
                    </button>
                )}
            </div>

            {/* Sales Table */}
            <div style={styles.box}>
                <div style={{ overflowX: 'auto' }}>
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={styles.th}>Fiş No</th>
                                <th style={styles.th}>Tarih</th>
                                <th style={styles.th}>Ürün Adedi</th>
                                <th style={styles.th}>Ödeme</th>
                                <th style={styles.th}>Durum</th>
                                <th style={{ ...styles.th, textAlign: 'right' }}>Tutar</th>
                                <th style={{ ...styles.th, textAlign: 'right' }}>İşlemler</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredSales.map(sale => (
                                <tr key={sale.id} style={{ opacity: sale.status === 'cancelled' ? 0.5 : 1 }}>
                                    <td style={{ ...styles.td, fontWeight: '500', color: 'var(--text-primary)' }}>
                                        #{sale.receiptNumber}
                                    </td>
                                    <td style={styles.td}>
                                        {sale.saleDate ? new Date(sale.saleDate.toDate()).toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' }) : '-'}
                                    </td>
                                    <td style={styles.td}>
                                        {(sale.items || []).reduce((s, i) => s + i.quantity, 0)}
                                    </td>
                                    <td style={styles.td}>
                                        <span style={{
                                            ...styles.badge,
                                            background: sale.paymentMethod === 'cash' ? '#dcfce7' : '#e0e7ff',
                                            color: sale.paymentMethod === 'cash' ? '#166534' : '#3730a3'
                                        }}>
                                            {sale.paymentMethod === 'cash' ? '💵 Nakit' : '💳 Kart'}
                                        </span>
                                    </td>
                                    <td style={styles.td}>
                                        <span style={{
                                            ...styles.badge,
                                            background: sale.status === 'cancelled' ? '#fee2e2' : '#dcfce7',
                                            color: sale.status === 'cancelled' ? '#991b1b' : '#166534'
                                        }}>
                                            {sale.status === 'cancelled' ? 'İptal' : 'Tamamlandı'}
                                        </span>
                                    </td>
                                    <td style={{ ...styles.td, textAlign: 'right', fontWeight: '600', color: 'var(--text-primary)' }}>
                                        {sale.discount > 0 && (
                                            <div style={{ fontSize: '11px', color: '#dc2626', fontWeight: '500', textDecoration: 'line-through' }}>
                                                ₺{(sale.subTotal || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                                            </div>
                                        )}
                                        ₺{(sale.total || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                                    </td>
                                    <td style={{ ...styles.td, textAlign: 'right' }}>
                                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                                            <button style={styles.actionBtn} onClick={() => setSelectedSale(sale)}>
                                                Detay
                                            </button>
                                            {sale.status !== 'cancelled' && (
                                                <button 
                                                    style={{ ...styles.actionBtn, color: '#dc2626', borderColor: '#fecaca' }}
                                                    onClick={() => setShowCancelConfirm(sale)}
                                                >
                                                    İptal
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {filteredSales.length === 0 && (
                                <tr>
                                    <td colSpan="7" style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)' }}>
                                        <div style={{ fontSize: '40px', marginBottom: '12px' }}>📋</div>
                                        <p>Satış kaydı bulunamadı.</p>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Sale Detail Modal */}
            {selectedSale && (
                <div style={styles.modalOverlay} onClick={() => setSelectedSale(null)}>
                    <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '24px', alignItems: 'center' }}>
                            <div>
                                <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '700' }}>Satış Detayı</h2>
                                <p style={{ color: 'var(--text-muted)', marginTop: '4px', fontSize: '14px' }}>#{selectedSale.receiptNumber}</p>
                            </div>
                            <button onClick={() => setSelectedSale(null)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: 'var(--text-muted)' }}>&times;</button>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
                            <div style={{ padding: '12px', background: 'var(--bg-secondary)', borderRadius: '8px' }}>
                                <div style={{ fontSize: '12px', color: 'var(--text-muted)', fontWeight: '600' }}>Tarih</div>
                                <div style={{ fontSize: '14px', fontWeight: '500', marginTop: '4px' }}>
                                    {selectedSale.saleDate ? new Date(selectedSale.saleDate.toDate()).toLocaleString('tr-TR') : '-'}
                                </div>
                            </div>
                            <div style={{ padding: '12px', background: 'var(--bg-secondary)', borderRadius: '8px' }}>
                                <div style={{ fontSize: '12px', color: 'var(--text-muted)', fontWeight: '600' }}>Ödeme Yöntemi</div>
                                <div style={{ fontSize: '14px', fontWeight: '500', marginTop: '4px' }}>
                                    {selectedSale.paymentMethod === 'cash' ? 'Nakit' : 'Kredi Kartı'}
                                </div>
                            </div>
                            <div style={{ padding: '12px', background: 'var(--bg-secondary)', borderRadius: '8px' }}>
                                <div style={{ fontSize: '12px', color: 'var(--text-muted)', fontWeight: '600' }}>Durum</div>
                                <div style={{ fontSize: '14px', fontWeight: '500', marginTop: '4px', color: selectedSale.status === 'cancelled' ? '#dc2626' : '#10b981' }}>
                                    {selectedSale.status === 'cancelled' ? '❌ İptal Edilmiş' : '✅ Tamamlandı'}
                                </div>
                            </div>
                            <div style={{ padding: '12px', background: 'var(--bg-secondary)', borderRadius: '8px' }}>
                                <div style={{ fontSize: '12px', color: 'var(--text-muted)', fontWeight: '600' }}>Satışı Yapan</div>
                                <div style={{ fontSize: '14px', fontWeight: '500', marginTop: '4px' }}>{selectedSale.soldBy?.email || '-'}</div>
                            </div>
                        </div>

                        <table style={{ ...styles.table, marginBottom: '16px' }}>
                            <thead>
                                <tr>
                                    <th style={styles.th}>Ürün</th>
                                    <th style={{ ...styles.th, textAlign: 'center' }}>Adet</th>
                                    <th style={{ ...styles.th, textAlign: 'right' }}>Birim Fiyat</th>
                                    <th style={{ ...styles.th, textAlign: 'right' }}>Tutar</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(selectedSale.items || []).map((item, idx) => (
                                    <tr key={idx}>
                                        <td style={{ ...styles.td, fontWeight: '500', color: 'var(--text-primary)' }}>{item.productName}</td>
                                        <td style={{ ...styles.td, textAlign: 'center' }}>{item.quantity}</td>
                                        <td style={{ ...styles.td, textAlign: 'right' }}>₺{(item.price || 0).toFixed(2)}</td>
                                        <td style={{ ...styles.td, textAlign: 'right', fontWeight: '600' }}>₺{(item.total || 0).toFixed(2)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        <div style={{ borderTop: '2px dashed var(--border-color)', paddingTop: '16px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', fontSize: '14px', color: 'var(--text-secondary)' }}>
                                <span>Alt Toplam:</span>
                                <span>₺{(selectedSale.subTotal || selectedSale.total || 0).toFixed(2)}</span>
                            </div>
                            {selectedSale.discount > 0 && (
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', fontSize: '14px', color: '#dc2626' }}>
                                    <span>İndirim ({selectedSale.discountType === 'percent' ? `%${selectedSale.discountValue}` : `₺${selectedSale.discountValue}`}):</span>
                                    <span>-₺{(selectedSale.discount || 0).toFixed(2)}</span>
                                </div>
                            )}
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '20px', fontWeight: '700', color: 'var(--text-primary)', marginTop: '8px' }}>
                                <span>Genel Toplam:</span>
                                <span>₺{(selectedSale.total || 0).toFixed(2)}</span>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Cancel Confirmation Modal */}
            {showCancelConfirm && (
                <div style={styles.modalOverlay} onClick={() => setShowCancelConfirm(null)}>
                    <div style={{ ...styles.modalContent, maxWidth: '420px' }} onClick={e => e.stopPropagation()}>
                        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
                            <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: 'var(--text-primary)' }}>Satış İptali</h2>
                            <p style={{ color: 'var(--text-muted)', marginTop: '8px', fontSize: '14px' }}>
                                Fiş No: <strong>#{showCancelConfirm.receiptNumber}</strong><br />
                                Tutar: <strong>₺{(showCancelConfirm.total || 0).toFixed(2)}</strong>
                            </p>
                            <p style={{ color: '#991b1b', fontSize: '14px', fontWeight: '500', background: '#fef2f2', padding: '12px', borderRadius: '8px', marginTop: '16px' }}>
                                Bu işlem geri alınamaz. Satıştaki ürünler envanterinize iade edilecektir.
                            </p>
                        </div>
                        <div style={{ display: 'flex', gap: '12px' }}>
                            <button 
                                onClick={() => setShowCancelConfirm(null)}
                                style={{ flex: 1, padding: '12px', background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: '10px', fontWeight: '600', cursor: 'pointer', fontSize: '14px', color: 'var(--text-secondary)' }}
                            >
                                Vazgeç
                            </button>
                            <button 
                                onClick={() => handleCancelSale(showCancelConfirm)}
                                disabled={actionLoading}
                                style={{ flex: 1, padding: '12px', background: '#dc2626', color: 'white', border: 'none', borderRadius: '10px', fontWeight: '600', cursor: 'pointer', fontSize: '14px', opacity: actionLoading ? 0.6 : 1 }}
                            >
                                {actionLoading ? 'İşleniyor...' : '❌ Satışı İptal Et'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
